// 📁 src/pages/WhatsTarot.js
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";

/* =========================================================
   THEME TOKENS (match suit pages/About.js)
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panels with brand-aligned gradient accents */
const SILK_INDIGO =
  "bg-[linear-gradient(135deg,rgba(20,33,61,0.85),rgba(17,51,89,0.85))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(75%_60%_at_80%_0%,rgba(118,141,255,0.30),transparent_100%)] before:content-['']";

const SILK_ROSE =
  "bg-[linear-gradient(135deg,rgba(60,24,44,0.85),rgba(90,28,60,0.85))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(70%_60%_at_15%_0%,rgba(255,149,214,0.28),transparent_100%)] before:content-['']";

const SILK_EMERALD =
  "bg-[linear-gradient(135deg,rgba(10,60,55,0.85),rgba(12,80,70,0.85))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(70%_60%_at_80%_0%,rgba(93,255,210,0.28),transparent_100%)] before:content-['']";

const SILK_SUNSET =
  "bg-[linear-gradient(135deg,rgba(70,30,20,0.85),rgba(90,40,18,0.85))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(75%_60%_at_80%_0%,rgba(255,196,86,0.28),transparent_100%)] before:content-['']";

const SILK_AZURE =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.88),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.30),transparent_100%)] before:content-['']";

/* =========================================================
   SEO (aligned with About.js)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/what-is-tarot-cards-complete-guide`;
const logoUrl = `${siteUrl}/icons/logo.webp`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;

const metaDescription =
  "The complete tarot guide. Learn what tarot is, how readings work, deck structure, suits and elements, numerology, court cards, spreads, ethics, reversals, and a step by step example. Clear guidance for love, career, and personal growth.";

const megaKeywords = `
  what is tarot, tarot guide, tarot for beginners, how tarot works, tarot meanings, major arcana, minor arcana,
  wands cups swords pentacles, suits and elements, tarot numerology, court cards explained, tarot spreads,
  three card spread, celtic cross, daily card, asking better tarot questions, tarot reversals, significators,
  clarifiers, tarot ethics, consent, tarot journal, cleansing a tarot deck, choosing a tarot deck,
  upright meanings, reversed meanings, symbolism in tarot, history of tarot, self reflection, spiritual growth,
  love readings, career readings, personal development, meditation with tarot, mindfulness with tarot,
  tarot in English, tarot en Español, tarot em Português, tarot en Français, tarot Deutsch,
  tarocchi Italiano, tarot Türkçe, tarot 日本語, tarot 한국어
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Copy blocks
========================================================= */
const heroLead =
  "Tarot is a mirror that reflects pattern and choice. This guide explains what tarot is, how a reading works, why the images speak with clarity, and how to begin with confidence whether you are reading for yourself or for others.";

const deckOverview =
  "A complete tarot deck contains seventy eight cards. The Major Arcana includes twenty two archetypes that describe the larger journey of growth and awakening. The Minor Arcana contains fifty six cards across the suits of Wands, Cups, Swords, and Pentacles. These suits describe real situations and practical decisions across energy and ambition, emotion and relationship, thought and communication, and work and resources.";

const howItWorksIntro =
  "A tarot reading is a focused conversation. You bring a sincere question. The spread arranges images that help you hear your own wisdom with precision. The process is reflective and practical and it supports action that aligns with your values.";

const ethicsBlock =
  "Tarot supports reflection and growth. It does not replace medical, legal, or financial guidance. Read with informed consent and protect privacy. Avoid determinism and invite agency. Encourage choices that honor health, safety, and core values.";

const chooseIntro =
  "Choose a deck that you enjoy looking at and that you will use often. Preference matters more than trend. Hold the cards, notice how the images make you feel, and choose a size that fits your hands.";

const careIntro =
  "Treat the deck with respect. Keep it dry and clean. Reset your attention before each reading. Some readers mark intention with light, sound, or time in nature. The method matters less than sincere attention.";

const elementsIntro =
  "The four suits map to the four classical elements. This framework helps you read across the deck with consistency and depth.";

const numerologyIntro =
  "Numbers add a steady layer of meaning. The sequence from Ace through Ten describes a full cycle from beginning to integration.";

const courtsIntro =
  "The court cards model roles and styles. They can describe you, another person, a way of acting, or a quality to cultivate in the moment.";

const questionsIntro =
  "A clear question produces a clear reading. Use language that invites awareness and choice. Avoid wording that removes agency or seeks control over others.";

const reversalsIntro =
  "Reversed cards are a reader choice. They can signal a block, a private process, or a reduced expression of the upright meaning. Use them if they help you see nuance.";

const exampleIntro =
  "This example shows a simple three card process for a common situation. Use it as a model and adjust to your needs.";

/* FAQ content for on page and JSON-LD */
const faqList = [
  { q: "What is tarot used for?", a: "Tarot is used for reflection, clarity, and decision support. It helps you name what matters, understand patterns, and choose the next step that aligns with your values." },
  { q: "Is tarot fortune telling?", a: "Tarot does not fix your future. It shows influences and options. Your choices shape outcome. The value of a reading is insight and aligned action." },
  { q: "Can I read for myself?", a: "Yes. Read for yourself with honesty and patience. If emotions are intense, ask a trusted reader to reflect with you or take a break and return when you feel steady." },
  { q: "Do I need to use reversed meanings?", a: "Use reversals if they add helpful nuance. If you prefer, read upright meanings and rely on position and context. Choose one approach and apply it with consistency." },
  { q: "How accurate are tarot readings?", a: "Accuracy improves with a clear question, an ethical process, and consistent practice. Keep a journal and compare impressions with results over time." },
  { q: "How do I ask a good tarot question?", a: "Ask open questions that you can act on. For example ask what supports progress or what choice aligns with your values. Avoid questions that control others." },
  { q: "What is a significator in tarot?", a: "A significator is a card chosen to represent the seeker or the focus of a reading. You can select it by court rank, zodiac, or intuitive fit. Using a significator is optional." },
  { q: "What is a clarifier card?", a: "A clarifier is an additional draw used when a position needs more detail. Use clarifiers with restraint and only after you have explored the original card fully." },
];

/* =========================================================
   Smooth offset scroll helper
========================================================= */
function scrollWithOffset(hash, smooth = true) {
  if (!hash) return;
  const el = document.querySelector(hash);
  if (!el) return;
  const navH = document.getElementById("site-nav")?.offsetHeight || 64;
  const extra = 8;
  const y = window.scrollY + el.getBoundingClientRect().top - navH - extra;
  const opts = smooth ? { top: Math.max(0, y), behavior: "smooth" } : { top: Math.max(0, y) };
  window.scrollTo(opts);
}

/* =========================================================
   JSON-LD (WebPage + FAQPage + Two HowTo guides)
========================================================= */
function buildSchemaGraph() {
  const faqEntities = faqList.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const howToDaily = {
    "@type": "HowTo",
    name: "How to do a daily one card tarot reading",
    description: "A simple daily practice that builds clarity and trust.",
    step: [
      { "@type": "HowToStep", name: "Form a question", text: "Ask what supports you today. Name your intention for clarity and care." },
      { "@type": "HowToStep", name: "Shuffle and draw", text: "Shuffle with attention. Draw one card." },
      { "@type": "HowToStep", name: "Describe what you see", text: "Note symbols, posture, color, and your first feeling." },
      { "@type": "HowToStep", name: "Choose one action", text: "Translate the card into one practical step you will take today." },
      { "@type": "HowToStep", name: "Record and review", text: "Write the result in a journal and review progress each week." },
    ],
  };

  const howToThreeCard = {
    "@type": "HowTo",
    name: "How to do a three card tarot reading",
    description: "A clear structure for Past, Present, and Future.",
    step: [
      { "@type": "HowToStep", name: "Set your focus", text: "Name the situation. Ask what supports wise choice now." },
      { "@type": "HowToStep", name: "Lay three cards", text: "Place cards left to right for Past, Present, and Future." },
      { "@type": "HowToStep", name: "Interpret positions", text: "Past gives context. Present names the core. Future shows likely direction." },
      { "@type": "HowToStep", name: "Synthesize", text: "Combine images and meanings to form one sentence of guidance." },
      { "@type": "HowToStep", name: "Act and reflect", text: "Choose a next step and record outcome to refine your process." },
    ],
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "What is Tarot? Complete Guide | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "What is Tarot", item: pageUrl },
        ],
      },
      { "@type": "FAQPage", "@id": `${pageUrl}#faq`, mainEntity: faqEntities },
      { "@type": "HowTo", "@id": `${pageUrl}#howto-daily`, ...howToDaily },
      { "@type": "HowTo", "@id": `${pageUrl}#howto-three-card`, ...howToThreeCard },
    ],
  };
}

export default function WhatsTarot() {
  useEffect(() => {
    if (window.location.hash) {
      requestAnimationFrame(() => scrollWithOffset(window.location.hash, false));
    }
    const onHashChange = () => scrollWithOffset(window.location.hash, true);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return (
    <div className={`${FONT} text-white min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      {/* SEO via shared component (consistent with About.js) */}
      <Seo
        title="What is Tarot? Complete Guide | TarotFate"
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={buildSchemaGraph()}
        keywords={megaKeywords}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(255,196,86,0.45)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">What Is Tarot?</h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2 text-center`}>{heroLead}</p>

        {/* quick chips */}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {[
            ["#deck-structure", "Deck structure"],
            ["#how-it-works", "How it works"],
            ["#spreads", "Spreads"],
            ["#choose", "Choose a deck"],
            ["#care", "Care"],
            ["#elements", "Elements"],
            ["#numerology", "Numerology"],
            ["#courts", "Court cards"],
            ["#questions", "Questions"],
            ["#reversals", "Reversals"],
            ["#example", "Example reading"],
            ["#myths", "Myths"],
            ["#faq", "FAQ"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                window.history.replaceState(null, "", href);
                scrollWithOffset(href, true);
              }}
              className="px-3 py-1.5 rounded-full bg-white/12 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition"
            >
              {label}
            </a>
          ))}
        </div>
      </section>

      {/* DECK STRUCTURE */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="deck-structure">
        <div className={`${PANEL_BASE} ${SILK_INDIGO} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ What Tarot Contains</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{deckOverview}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href="/tarot/major-arcana-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">Major Arcana</a>
              <a href="/tarot/wands-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">Wands</a>
              <a href="/tarot/cups-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">Cups</a>
              <a href="/tarot/swords-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">Swords</a>
              <a href="/tarot/pentacles-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">Pentacles</a>
              <a href="/tarot-cards" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">All cards</a>
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="how-it-works">
        <div className={`${PANEL_BASE} ${SILK_ROSE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ How a Reading Works</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{howItWorksIntro}</p>

            <div className="mt-5 grid md:grid-cols-3 gap-3 sm:gap-4">
              {[
                { t: "Set intention and form a question", d: "Name a sincere and actionable focus. Breathe and state your aim for clarity and care." },
                { t: "Shuffle and draw cards", d: "Draw one card for a daily focus or three cards for Past, Present, and Future." },
                { t: "Describe the image", d: "Note symbols, posture, color, and your first feeling before checking any guide." },
                { t: "Integrate meaning", d: "Combine impressions with the role of the position to form a clear message." },
                { t: "Choose one next step", d: "Translate guidance into a practical action that you will take within a realistic time frame." },
                { t: "Journal and review", d: "Record result and return weekly to refine your understanding and accuracy." },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* BEGINNER SPREADS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="spreads">
        <div className={`${PANEL_BASE} ${SILK_SUNSET} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Simple Spreads for Beginners</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>
              Start small and build trust in your process. These spreads are clear and flexible.
            </p>
            <div className="mt-3 grid md:grid-cols-3 gap-3 sm:gap-4">
              {[
                { t: "Single card daily pull", d: "Ask what supports you today. Draw one card and name one action you will take before the day ends." },
                { t: "Past, Present, and Future", d: "Three cards show context, current focus, and likely direction. Use the message to choose the next step." },
                { t: "Choice A and B", d: "Two cards outline each option. A third card offers guidance that helps you decide with integrity." },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* CHOOSING A DECK */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="choose">
        <div className={`${PANEL_BASE} ${SILK_EMERALD} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Choosing a Deck</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{chooseIntro}</p>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {[
                "Pick a style that invites curiosity and steady use.",
                "Consider card stock, finish, and size for regular shuffling.",
                "A guidebook can help at first, yet your notes matter most.",
                "You may buy your first deck for yourself with confidence.",
              ].map((line, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <p className={`${TEXT} text-sm`}>{line}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* CARE AND CLEANSING */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="care">
        <div className={`${PANEL_BASE} ${SILK_AZURE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Caring for a Deck</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{careIntro}</p>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {[
                "Store the deck in a clean dry place and keep it together in a box or wrap.",
                "Begin with breath, a short prayer, or a clear intention for service.",
                "Use a cloth for reading and keep a simple journal for results.",
                "If you like, mark intention with light, gentle sound, or time in nature.",
              ].map((line, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <p className={`${TEXT} text-sm`}>{line}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* ELEMENTS AND CORRESPONDENCES */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="elements">
        <div className={`${PANEL_BASE} ${SILK_INDIGO} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Suits and Elements</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{elementsIntro}</p>
            <div className="mt-3 grid md:grid-cols-4 gap-3">
              {[
                { t: "Wands • Fire", d: "Energy, initiative, leadership, creativity, visibility, movement." },
                { t: "Cups • Water", d: "Emotion, relationship, intuition, empathy, healing, imagination." },
                { t: "Swords • Air", d: "Thought, clarity, truth, language, analysis, choice, ethics." },
                { t: "Pentacles • Earth", d: "Body, work, money, craft, skill, home, time, steady growth." },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* NUMEROLOGY */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="numerology">
        <div className={`${PANEL_BASE} ${SILK_SUNSET} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Numbers in Tarot</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{numerologyIntro}</p>
            <div className="mt-3 grid md:grid-cols-5 gap-3">
              {[
                ["Ace", "Source, spark, gift of pure potential."],
                ["Two", "Choice, balance, polarity, partnership."],
                ["Three", "Growth, synthesis, early result."],
                ["Four", "Stability, structure, rest and review."],
                ["Five", "Change, challenge, test of values."],
                ["Six", "Flow, harmony, recovery, support."],
                ["Seven", "Assessment, strategy, patience."],
                ["Eight", "Skill, power, practice, movement."],
                ["Nine", "Attainment, refinement, near completion."],
                ["Ten", "Culmination, transition, handoff to a new cycle."],
              ].map(([n, d], i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{n}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* COURT CARDS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="courts">
        <div className={`${PANEL_BASE} ${SILK_ROSE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Court Cards Explained</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{courtsIntro}</p>
            <div className="mt-3 grid md:grid-cols-4 gap-3">
              {[
                ["Page", "Student energy, curiosity, message, beginning a skill."],
                ["Knight", "Movement, mission, style of pursuit, momentum."],
                ["Queen", "Mastery with empathy, inner authority, stewardship."],
                ["King", "Structure, vision, accountability, public leadership."],
              ].map(([rank, d], i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{rank}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* ASKING BETTER QUESTIONS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="questions">
        <div className={`${PANEL_BASE} ${SILK_AZURE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Asking Better Questions</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{questionsIntro}</p>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/20 bg-white/10 p-3">
                <h3 className="text-sm font-semibold text-white/95">Helpful forms</h3>
                <ul className="mt-1 space-y-1 text-sm">
                  {[
                    "What supports progress in this situation",
                    "What choice aligns with my values",
                    "What can I learn from this conflict",
                    "What is the next wise step I can take",
                  ].map((s, i) => (
                    <li key={i} className={TEXT}>{s}?</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-white/20 bg-white/10 p-3">
                <h3 className="text-sm font-semibold text-white/95">Avoid</h3>
                <ul className="mt-1 space-y-1 text-sm">
                  {[
                    "Questions that control another persons choices",
                    "Yes or no questions that block reflection",
                    "Deterministic questions that remove agency",
                    "Questions asked in a state of harm or risk",
                  ].map((s, i) => (
                    <li key={i} className={TEXT}>{s}.</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* REVERSALS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="reversals">
        <div className={`${PANEL_BASE} ${SILK_EMERALD} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Reversals and Clarifiers</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{reversalsIntro}</p>
            <div className="mt-3 grid md:grid-cols-3 gap-3">
              {[
                { t: "Working with reversals", d: "Decide a method. Read reversed as a block or as a private process or as a reduced expression. Apply the choice with consistency." },
                { t: "When to draw a clarifier", d: "Draw one clarifier only after you have worked the original card. Ask a specific question for the clarifier and stop after one." },
                { t: "Significators", d: "Select a card to represent the seeker or the theme if it helps you focus. Use by intuition or choose by rank and element." },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* EXAMPLE READING */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="example">
        <div className={`${PANEL_BASE} ${SILK_SUNSET} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Example Reading Walkthrough</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{exampleIntro}</p>
            <div className="mt-3 grid md:grid-cols-3 gap-3">
              {[
                { t: "Question", d: "What supports wise choice about a new role at work" },
                { t: "Spread", d: "Three cards for Past, Present, and Future." },
                { t: "Cards drawn", d: "Past is Three of Pentacles. Present is Two of Wands. Future is The Chariot." },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {[
                {
                  t: "Interpretation",
                  d: "Past shows collaboration and growing skill. Present shows vision and a need to choose a direction. Future shows focused movement and commitment. Together the cards suggest that you are ready to lead a project that matches your values and strengths.",
                },
                {
                  t: "One next step",
                  d: "Define the decision in writing. List needs and constraints. Schedule one conversation with a mentor and one with the team to align scope and support.",
                },
              ].map(({ t, d }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">{t}</h3>
                  <p className={`${TEXT} text-sm mt-1`}>{d}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* COMMON MYTHS */}
      <section className={`mt-6 ${WRAP} ${MAXW}`} id="myths">
        <div className={`${PANEL_BASE} ${SILK_ROSE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold text-white/95">✦ Common Myths</h2>
            <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>
              Many myths surround tarot. Clearing them away helps you use the deck with confidence and respect.
            </p>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              {[
                { myth: "You must be given your first deck.", truth: "You may buy your own deck. Choose one that you like and will use." },
                { myth: "Tarot fixes the future.", truth: "Tarot describes influences and options. Choice and action shape the result." },
                { myth: "Only gifted psychics can read.", truth: "Anyone can learn. Practice, reflection, and ethics build skill and trust." },
                { myth: "Reversals are mandatory.", truth: "Reversals are optional. Consistency in your method matters most." },
              ].map(({ myth, truth }, i) => (
                <div key={i} className="rounded-xl border border-white/20 bg-white/10 p-3">
                  <h3 className="text-sm font-semibold text-white/95">Myth</h3>
                  <p className={`${TEXT} text-sm`}>{myth}</p>
                  <h4 className="text-sm font-semibold text-white/95 mt-2">What is true</h4>
                  <p className={`${TEXT} text-sm`}>{truth}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_AZURE} p-4 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold">Frequently Asked Questions</h2>
            <div className="mt-4 rounded-sm border border-white/30 bg-white/10 divide-y divide-white/20">
              {faqList.map(({ q, a }, i) => (
                <details key={i} className="group">
                  <summary
                    className="
                      cursor-pointer list-none px-4 md:px-6 py-3
                      text-sm sm:text-sm text-white/95 font-medium
                      flex items-start justify-between gap-6
                      after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                      group-open:after:content-['−']
                    "
                  >
                    <span className="pr-2">{q}</span>
                  </summary>
                  <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>{a}</div>
                </details>
              ))}
            </div>

            {/* Explore cards */}
            <div className="text-center mt-8">
              <div className="relative inline-flex flex-wrap justify-center gap-2 sm:gap-3">
                <a href="/tarot/major-arcana-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ Major Arcana</a>
                <a href="/tarot/wands-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ Wands</a>
                <a href="/tarot/cups-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ Cups</a>
                <a href="/tarot/swords-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ Swords</a>
                <a href="/tarot/pentacles-tarot-suit-cards-meaning" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ Pentacles</a>
                <a href="/tarot-cards" className="px-3 py-1.5 rounded-full bg-white/10 border border-white/25 text-white/95 text-sm font-medium hover:bg-white/20 hover:border-white/35 transition">✦ All Cards</a>
              </div>
            </div>

            {/* Ethics note */}
            <p className={`${TEXT} text-sm mt-6`}>{ethicsBlock}</p>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-70 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
      </section>
    </div>
  );
}
